import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronRight, Loader2, CalendarDays, Crown, CopyPlus, Plus, HelpCircle, Activity, UserCog, TrendingDown, X, Utensils, Dumbbell, PlayCircle, RefreshCw, Flame } from 'lucide-react';
import { toast } from 'sonner';
import { useClientAuth } from '../../context/ClientAuthContext';
import { useTourRegister } from '../../utils/tourNav';
import { ProgressRings } from './ProgressRings';
import { MealList } from './MealList';
import { AddFoodSheet } from './AddFoodSheet';
import { CoachingPanel } from './CoachingPanel';
import { GuideModal } from './GuideModal';
import { GoalsCard } from './GoalsCard';
import { NutritionCharts } from './NutritionCharts';
import { MeasurementsCard } from './MeasurementsCard';
import { ProgressPhotos } from './ProgressPhotos';
import { ProfileCard } from './ProfileCard';
import { GamificationBar } from './GamificationBar';
import { WaterTracker } from './WaterTracker';
import { WeightQuickWidget } from './WeightQuickWidget';
import { ActivityWidget } from './ActivityWidget';
import { MoodWidget } from './MoodWidget';
import { DayDetailView } from './DayDetailView';
import { QuickStartCard } from './QuickStartCard';
import { BadgesStrip, BADGE_META } from './BadgesStrip';
import { NPSCard } from './NPSCard';
import { SmartTooltip } from '../../components/SmartTooltip';
import { todayStr, addDays } from './trackingConstants';
import { getWithExpiry } from '../../utils/storage';

const WeightSparkline = ({ points }) => {
  const w = 140, h = 32, pad = 3;
  const vals = points.map((p) => p.weight);
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min || 1;
  const coords = points.map((p, i) => {
    const x = pad + (i / (points.length - 1)) * (w - pad * 2);
    const y = h - pad - ((p.weight - min) / range) * (h - pad * 2);
    return `${x},${y}`;
  });
  return (
    <svg width={w} height={h} className="mt-2" data-testid="weight-alert-sparkline">
      <polyline points={coords.join(' ')} fill="none" stroke="#FBBF24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={coords[coords.length - 1].split(',')[0]} cy={coords[coords.length - 1].split(',')[1]} r="3" fill="#FBBF24" />
    </svg>
  );
};

const EMPTY_SUMMARY = {
  totals: { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 },
  goals: { calories: 2000, protein: 130, carbs: 220, fat: 65 },
  remaining: { calories: 2000, protein: 130, carbs: 220, fat: 65 },
  percent: { calories: 0, protein: 0, carbs: 0, fat: 0 },
  health_score: null, entry_count: 0,
};

const EMPTY_EXTRAS = {
  water_ml: 0, water_goal_ml: 2000, steps: 0, activity_kcal: 0, steps_goal: 8000,
  mood: null, note_text: '', gems: 0, current_streak: 0, longest_streak: 0,
  workout_current_streak_weeks: 0, workout_longest_streak_weeks: 0, badges: [], newly_unlocked_badges: [],
};

export const TrackingDashboard = ({ en, onNavigate }) => {
  const { authFetch } = useClientAuth();
  const navigate = useNavigate();
  const [date, setDate] = useState(todayStr());
  const [day, setDay] = useState(null);
  const [extras, setExtras] = useState(EMPTY_EXTRAS);
  const [weightEntries, setWeightEntries] = useState([]);
  const [premium, setPremium] = useState(false);
  const [sheet, setSheet] = useState({ open: false, meal: null });
  const [guide, setGuide] = useState(false);
  const [view, setView] = useState('today');
  const [loading, setLoading] = useState(true);
  const [profileStatus, setProfileStatus] = useState(null);
  useTourRegister('setTrackingView', setView);
  
  // Refs pour les tooltips
  const addFoodBtnRef = useRef(null);
  const copyBtnRef = useRef(null);
  
  // États des tooltips (affichage progressif)
  const [showAddFoodTooltip, setShowAddFoodTooltip] = useState(false);
  const [showCopyTooltip, setShowCopyTooltip] = useState(false);

  const api = useCallback(
    (path, opts) => authFetch(path, opts).then((r) => (r.ok ? r.json() : Promise.reject(r))),
    [authFetch]
  );

  const loadDay = useCallback(() => {
    setLoading(true);
    api(`/api/tracking/day?date=${date}&lang=${en ? 'en' : 'fr'}`)
      .then((d) => setDay(d))
      .catch(() => setDay({ entries: [], summary: EMPTY_SUMMARY }))
      .finally(() => setLoading(false));
  }, [api, date, en]);

  const loadExtras = useCallback(() => {
    api(`/api/tracking/journal-extras?date=${date}`).then((d) => {
      setExtras(d);
      (d.newly_unlocked_badges || []).forEach((key) => {
        const meta = BADGE_META[key];
        if (meta) toast.success(en ? `Badge unlocked: ${meta.labelEn}` : `Badge débloqué : ${meta.labelFr}`, { description: en ? meta.descEn : meta.descFr });
      });
    }).catch(() => setExtras(EMPTY_EXTRAS));
  }, [api, date, en]);

  const loadWeight = useCallback(() => {
    api('/api/tracking/weight').then((d) => setWeightEntries(d.entries || [])).catch(() => {});
  }, [api]);

  const loadProfileStatus = useCallback(() => {
    api('/api/tracking/profile').then((d) => setProfileStatus(d)).catch(() => {});
  }, [api]);

  // Onboarding Éclair — carte de bienvenue 3 étapes (accueil), tous plans app.
  const [quickstart, setQuickstart] = useState(null);
  const loadQuickstart = useCallback(() => {
    api('/api/tracking/quickstart').then(setQuickstart).catch(() => {});
  }, [api]);
  useEffect(() => { loadQuickstart(); }, [loadQuickstart]);
  const dismissQuickstart = useCallback(() => {
    setQuickstart((q) => (q ? { ...q, dismissed: true } : q));
    api('/api/tracking/quickstart/dismiss', { method: 'POST' }).catch(() => {});
  }, [api]);

  const [plateauSuggestion, setPlateauSuggestion] = useState(null);
  const loadPlateau = useCallback(() => {
    api('/api/tracking/plateau-suggestion').then((d) => setPlateauSuggestion(d.suggestion || null)).catch(() => {});
  }, [api]);
  const dismissPlateau = useCallback(() => {
    setPlateauSuggestion(null);
    api('/api/tracking/plateau-suggestion/dismiss', { method: 'POST' }).catch(() => {});
  }, [api]);

  // Coach Séance Rapide — la séance la plus "en retard" (ratio séances faites/objectif le plus bas)
  // du premier programme actif, pour un démarrage en un clic depuis l'onglet Aujourd'hui.
  const [nextSession, setNextSession] = useState(null);
  useEffect(() => {
    api('/api/tracking/programs-pro').then((d) => {
      const program = (d.programs || [])[0];
      const sessions = (program?.sessions || []).filter((s) => s.done_count < (s.sessions_target || 1));
      if (d.locked || !sessions.length) { setNextSession(null); return; }
      const sorted = [...sessions].sort(
        (a, b) => (a.done_count / (a.sessions_target || 1)) - (b.done_count / (b.sessions_target || 1))
      );
      const s = sorted[0];
      setNextSession({ programId: program.program_id, sessionLabel: s.session_label, doneCount: s.done_count, target: s.sessions_target });
    }).catch(() => setNextSession(null));
  }, [api]);

  // Widget Objectif Semaine — jauges séances (types de séances du programme actif) + jours
  // avec repas loggé, semaine calendaire (lundi -> aujourd'hui).
  const [weekGoals, setWeekGoals] = useState(null);
  const loadWeekGoals = useCallback(() => {
    api('/api/tracking/week-goals').then(setWeekGoals).catch(() => setWeekGoals(null));
  }, [api]);
  useEffect(() => { loadWeekGoals(); }, [loadWeekGoals]);

  // Badge "Série en cours" + mini-graphique 4 semaines (objectif = les 2 jauges à 100%)
  const [weekHistory, setWeekHistory] = useState(null);
  const loadWeekHistory = useCallback(() => {
    api('/api/tracking/week-goals-history?weeks=12').then(setWeekHistory).catch(() => setWeekHistory(null));
  }, [api]);
  useEffect(() => { loadWeekHistory(); }, [loadWeekHistory]);

  // Pull-to-refresh natif (mobile) — on ne transforme JAMAIS un ancestor via CSS `transform`
  // ici (ça casserait le positionnement `fixed` des modales enfants comme AddFoodSheet) : on
  // fait juste grandir un espaceur en flux normal au-dessus du contenu pendant le tirage.
  const touchStartY = useRef(null);
  const [pull, setPull] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const PULL_THRESHOLD = 64;
  const onTouchStartRoot = (e) => {
    touchStartY.current = window.scrollY <= 2 && !refreshing ? e.touches[0].clientY : null;
  };
  const onTouchMoveRoot = (e) => {
    if (touchStartY.current == null) return;
    const delta = e.touches[0].clientY - touchStartY.current;
    if (delta < 0) { touchStartY.current = null; setDragging(false); setPull(0); return; }
    setDragging(true);
    setPull(Math.min(delta * 0.5, 90));
  };
  const onTouchEndRoot = async () => {
    setDragging(false);
    if (pull > PULL_THRESHOLD && !refreshing) {
      setRefreshing(true);
      setPull(52);
      loadDay(); loadExtras(); loadWeight(); loadProfileStatus(); loadPlateau(); loadWeekGoals(); loadWeekHistory();
      await new Promise((r) => setTimeout(r, 700));
      setRefreshing(false);
    }
    setPull(0);
    touchStartY.current = null;
  };

  useEffect(() => { loadDay(); loadExtras(); }, [loadDay, loadExtras]);
  useEffect(() => { loadWeight(); }, [loadWeight]);
  useEffect(() => { loadProfileStatus(); }, [loadProfileStatus]);
  useEffect(() => { loadPlateau(); }, [loadPlateau]);

  // La modale guide ne s'ouvre PAS automatiquement (l'overlay bloquait les onglets).
  // Elle est désormais accessible exclusivement via le bouton « Guide & conseils ».
  // Aucun localStorage n'est touché ici : la modale gère elle-même le flag
  // `bodyup_guide_dismissed` quand l'utilisateur clique la case « Ne plus afficher ».
  
  // Affichage progressif des tooltips (après 3 secondes si pas déjà vu)
  useEffect(() => {
    if (!day || loading) return;
    
    const addFoodSeen = getWithExpiry('bodyup_tooltip_add-food');
    if (!addFoodSeen) {
      const timer = setTimeout(() => setShowAddFoodTooltip(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [day, loading]);

  useEffect(() => {
    api('/api/tracking/subscription').then((d) => setPremium(!!d.premium)).catch(() => {});
  }, [api]);

  const onUpgrade = () => navigate('/suivi-pro');

  const deleteEntry = async (id) => {
    await api(`/api/tracking/log/${id}`, { method: 'DELETE' }).catch(() => {});
    loadDay();
  };

  const favoriteEntry = async (e) => {
    try {
      await api('/api/tracking/favorites', {
        method: 'POST',
        body: JSON.stringify({
          name: e.food_name, source: e.source, calories: e.calories, protein: e.protein,
          carbs: e.carbs, fat: e.fat, fiber: e.fiber, health_score: e.health_score,
          quantity: e.quantity, unit: e.unit,
        }),
      });
      toast.success(en ? 'Added to favorites' : 'Ajouté aux favoris');
    } catch {
      toast.error(en ? 'Could not save favorite' : 'Impossible d\'enregistrer le favori');
    }
  };

  const copyYesterday = async () => {
    try {
      const d = await api('/api/tracking/day/copy', {
        method: 'POST',
        body: JSON.stringify({ from_date: addDays(date, -1), to_date: date }),
      });
      if (d.copied > 0) { toast.success(en ? `${d.copied} item(s) copied from yesterday` : `${d.copied} aliment(s) copié(s) de la veille`); loadDay(); }
      else toast.info(en ? 'Nothing logged yesterday' : 'Rien d\'enregistré la veille');
    } catch {
      toast.error(en ? 'Copy failed' : 'Copie impossible');
    }
  };

  const addWater = async (ml) => {
    try {
      const d = await api('/api/tracking/water', { method: 'POST', body: JSON.stringify({ date, ml }) });
      setExtras((p) => ({ ...p, water_ml: d.water_ml }));
      loadExtras();
    } catch { toast.error(en ? 'Error' : 'Erreur'); }
  };

  const setWaterGoal = async (goal_ml) => {
    try {
      await api('/api/tracking/water/goal', { method: 'PUT', body: JSON.stringify({ goal_ml }) });
      setExtras((p) => ({ ...p, water_goal_ml: goal_ml }));
    } catch { toast.error(en ? 'Error' : 'Erreur'); }
  };

  const addActivity = async (steps, calories_burned) => {
    try {
      await api('/api/tracking/activity', { method: 'POST', body: JSON.stringify({ date, steps, calories_burned }) });
      toast.success(en ? 'Activity logged' : 'Activité enregistrée');
      loadExtras();
    } catch { toast.error(en ? 'Error' : 'Erreur'); }
  };

  const saveMood = async (mood, text) => {
    try {
      await api('/api/tracking/note', { method: 'PUT', body: JSON.stringify({ date, mood, text }) });
      toast.success(en ? 'Note saved' : 'Note enregistrée');
      loadExtras();
    } catch { toast.error(en ? 'Error' : 'Erreur'); }
  };

  const notifyWeightAlert = (alert) => {
    if (!alert) return;
    const sign = alert.diff > 0 ? '+' : '';
    const last7 = [...weightEntries.filter((e) => e.weight != null).slice(-6), { date: 'now', weight: alert.new_weight }];
    toast.custom(() => (
      <div data-testid="weight-anomaly-toast" className="bg-amber-950/95 border border-amber-500/40 rounded-xl p-4 shadow-xl max-w-sm">
        <p className="text-sm font-bold text-amber-300 mb-1">{en ? 'Unusual weight change detected' : 'Écart de poids inhabituel détecté'}</p>
        <p className="text-xs text-amber-200/80">
          {en
            ? `${alert.previous_weight} kg → ${alert.new_weight} kg (${sign}${alert.diff} kg, ${sign}${alert.pct}%). Double-check your entry?`
            : `${alert.previous_weight} kg → ${alert.new_weight} kg (${sign}${alert.diff} kg, ${sign}${alert.pct}%). Vérifiez votre saisie ?`}
        </p>
        {last7.length > 2 && <WeightSparkline points={last7} />}
      </div>
    ), { duration: 10000 });
  };

  const logWeightToday = async (weight) => {
    try {
      const res = await api('/api/tracking/weight', { method: 'POST', body: JSON.stringify({ date, weight }) });
      toast.success(en ? 'Weight logged' : 'Poids enregistré');
      notifyWeightAlert(res?.weight_alert);
      loadWeight();
      loadExtras();
      loadDay();
      loadProfileStatus();
    } catch { toast.error(en ? 'Error' : 'Erreur'); }
  };

  const isToday = date === todayStr();
  const dateLabel = new Date(date + 'T12:00:00').toLocaleDateString(en ? 'en-GB' : 'fr-CH', { weekday: 'long', day: '2-digit', month: 'long' });
  const summary = day?.summary || EMPTY_SUMMARY;
  const lastWeightEntry = weightEntries.length ? weightEntries[weightEntries.length - 1] : null;

  // Parallax doux des orbes de fond au scroll (ambiance premium)
  const { scrollY } = useScroll();
  const orb1Y = useTransform(scrollY, [0, 1200], [0, 180]);
  const orb2Y = useTransform(scrollY, [0, 1200], [0, -140]);

  return (
    <div data-testid="tracking-dashboard" className="relative" onTouchStart={onTouchStartRoot} onTouchMove={onTouchMoveRoot} onTouchEnd={onTouchEndRoot}>
      <div
        className="flex items-center justify-center overflow-hidden"
        style={{ height: refreshing ? 44 : pull, transition: dragging ? 'none' : 'height 0.25s ease-out' }}
        data-testid="pull-to-refresh-indicator"
      >
        <RefreshCw
          className={`w-5 h-5 text-primary ${refreshing ? 'animate-spin' : ''}`}
          style={refreshing ? undefined : { transform: `rotate(${Math.min((pull / PULL_THRESHOLD) * 180, 180)}deg)`, opacity: Math.min(pull / PULL_THRESHOLD, 1) }}
        />
      </div>
      <div className="pointer-events-none fixed inset-0 overflow-hidden -z-10" aria-hidden="true">
        <motion.div style={{ y: orb1Y }} className="absolute -top-32 right-0 w-[26rem] h-[26rem] bg-primary/15 rounded-full blur-[130px]" />
        <motion.div style={{ y: orb2Y }} className="absolute top-1/2 -left-32 w-[24rem] h-[24rem] bg-purple-600/12 rounded-full blur-[130px]" />
      </div>
      {/* Top bar : titre de l'onglet + bouton Guide (réactivable) */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="text-xl font-black text-white flex items-center gap-2" data-testid="tracking-dashboard-title">
          <Utensils className="w-5 h-5 text-primary" /> {en ? 'My tracking' : 'Mon suivi'}
        </h2>
        <button
          onClick={() => setGuide(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-zinc-300 border border-white/10 hover:border-primary/50 hover:text-white transition-colors"
          data-testid="open-guide-btn"
        >
          <HelpCircle className="w-3.5 h-3.5" /> {en ? 'Guide & tips' : 'Guide & conseils'}
        </button>
      </div>

      {/* Onboarding Éclair — carte 3 étapes juste après un paiement (tous plans app) */}
      {view === 'today' && quickstart && !quickstart.dismissed && profileStatus && (
        <QuickStartCard
          en={en}
          steps={[
            { key: 'profile', label: en ? 'Complete your profile' : 'Complétez votre profil', Icon: UserCog, tab: 'profil', done: (profileStatus.missing_fields || []).length === 0 },
            { key: 'activity', label: en ? 'Log your first meal or weight' : 'Ajoutez votre 1er repas ou poids', Icon: Utensils, tab: 'nutrition', done: (day?.entries?.length || 0) > 0 || weightEntries.length > 0 },
            { key: 'workout', label: en ? 'Start your first workout' : 'Démarrez votre 1er entraînement', Icon: Dumbbell, tab: 'workouts', done: !!quickstart.has_workout },
          ]}
          onNavigate={(tab) => onNavigate && onNavigate(tab)}
          onDismiss={dismissQuickstart}
        />
      )}

      {view === 'today' && <NPSCard en={en} api={api} />}

      {/* Premium banner */}
      {!premium && (
        <motion.button
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          onClick={onUpgrade}
          className="w-full mb-6 rounded-2xl border border-yellow-500/30 bg-gradient-to-r from-yellow-500/10 to-primary/10 px-5 py-3.5 flex items-center justify-between gap-3 hover:border-yellow-500/50 transition-colors text-left"
          data-testid="premium-banner"
        >
          <div className="flex items-center gap-3">
            <Crown className="w-5 h-5 text-yellow-400 shrink-0" />
            <p className="text-sm text-white font-semibold">{en ? 'Suivi Pro: unlimited AI + photo recognition' : 'Suivi Pro : AI illimitée + reconnaissance photo'}</p>
          </div>
          <span className="text-xs font-bold text-black bg-gradient-to-r from-yellow-400 to-primary px-3 py-1.5 rounded-full whitespace-nowrap">{en ? 'Discover' : 'Découvrir'}</span>
        </motion.button>
      )}

      {/* Suggestion IA de plateau — s'affiche si le poids est resté quasi stable (±1kg)
          sur 3 semaines de pesées régulières. Bannière discrète, jamais bloquante. */}
      {view === 'today' && plateauSuggestion && (
        <motion.div
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="w-full mb-6 rounded-2xl border border-purple-500/30 bg-gradient-to-r from-purple-500/10 to-primary/10 px-5 py-3.5 flex items-start gap-3"
          data-testid="plateau-suggestion-banner"
        >
          <TrendingDown className="w-5 h-5 text-purple-300 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-purple-300 uppercase tracking-wide mb-1">{en ? 'Plateau detected' : 'Plateau détecté'}</p>
            <p className="text-sm text-white leading-snug">{plateauSuggestion.message}</p>
            <button onClick={() => setView('profile')} className="text-xs font-bold text-primary mt-1.5 hover:underline" data-testid="plateau-banner-see-profile">
              {en ? 'See details in Profile' : 'Voir le détail dans Profil'}
            </button>
          </div>
          <button onClick={dismissPlateau} className="shrink-0 text-zinc-400 hover:text-white" data-testid="plateau-banner-dismiss">
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      )}

      {/* Rappel profil incomplet — les objectifs affichés sont approximatifs (défaut) tant
          que les infos essentielles (âge/taille/poids/objectif/entraînement) ne sont pas connues. */}
      {view === 'today' && profileStatus && (profileStatus.missing_fields || []).length > 0 && (
        <motion.button
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          onClick={() => setView('profile')}
          className="w-full mb-6 rounded-2xl border border-sky-500/30 bg-gradient-to-r from-sky-500/10 to-primary/10 px-5 py-3.5 flex items-center justify-between gap-3 hover:border-sky-500/50 transition-colors text-left"
          data-testid="incomplete-profile-banner"
        >
          <div className="flex items-center gap-3">
            <UserCog className="w-5 h-5 text-sky-400 shrink-0" />
            <p className="text-sm text-white font-semibold">{en ? 'Complete your profile for accurate calorie targets' : 'Complétez votre profil pour des objectifs caloriques précis'}</p>
          </div>
          <span className="text-xs font-bold text-black bg-gradient-to-r from-sky-400 to-primary px-3 py-1.5 rounded-full whitespace-nowrap">{en ? 'Complete' : 'Compléter'}</span>
        </motion.button>
      )}

      {/* Coach Séance Rapide — démarrer en un clic la séance la plus en retard du programme actif */}
      {view === 'today' && nextSession && onNavigate && (
        <motion.button
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          onClick={() => onNavigate('workouts', { programId: nextSession.programId, sessionLabel: nextSession.sessionLabel })}
          className="w-full mb-6 rounded-2xl border border-primary/30 bg-gradient-to-r from-primary/15 to-purple-600/10 px-5 py-4 flex items-center justify-between gap-3 hover:border-primary/50 transition-colors text-left"
          data-testid="quick-start-session-card"
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-pink-600 flex items-center justify-center shrink-0">
              <Dumbbell className="w-5 h-5 text-white" />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-bold text-primary uppercase tracking-wide">{en ? 'Next session' : 'Prochaine séance'}</p>
              <p className="text-sm font-bold text-white truncate">{nextSession.sessionLabel} · {nextSession.doneCount}/{nextSession.target} {en ? 'sessions' : 'séances'}</p>
            </div>
          </div>
          <span className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold text-white bg-gradient-to-r from-primary to-pink-500 px-3.5 py-2 rounded-full whitespace-nowrap">
            <PlayCircle className="w-3.5 h-3.5" /> {en ? 'Start' : 'Démarrer'}
          </span>
        </motion.button>
      )}

      {/* Widget Objectif Semaine — jauges séances (types de séances du programme) + jours avec repas loggé */}
      {view === 'today' && weekGoals && (weekGoals.workouts.target > 0 || weekGoals.nutrition.target > 0) && (
        <motion.div
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="w-full mb-6 rounded-2xl border border-white/10 bg-white/[0.035] backdrop-blur-xl p-5"
          data-testid="week-goals-widget"
        >
          <div className="flex items-center justify-between gap-2 mb-3">
            <p className="text-xs font-bold text-zinc-400 uppercase tracking-wide">{en ? 'This week' : 'Objectif de la semaine'}</p>
            {weekHistory?.streak > 0 && (
              <span
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-gradient-to-r from-orange-500/20 to-amber-500/20 border border-orange-500/40 text-orange-300 whitespace-nowrap"
                data-testid="streak-badge"
              >
                <Flame className="w-3 h-3" /> {weekHistory.streak} {en
                  ? (weekHistory.streak > 1 ? 'weeks in a row' : 'week in a row')
                  : (weekHistory.streak > 1 ? "semaines d'affilée" : "semaine d'affilée")}
              </span>
            )}
          </div>
          <div className="space-y-3.5">
            {weekGoals.workouts.target > 0 && (
              <div data-testid="week-goal-workouts">
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="flex items-center gap-1.5 text-white font-semibold"><Dumbbell className="w-3.5 h-3.5 text-primary" /> {en ? 'Sessions' : 'Séances'}</span>
                  <span className="text-zinc-400 font-mono text-xs">{weekGoals.workouts.done}/{weekGoals.workouts.target}</span>
                </div>
                <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary to-pink-500 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((weekGoals.workouts.done / weekGoals.workouts.target) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}
            <div data-testid="week-goal-nutrition">
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="flex items-center gap-1.5 text-white font-semibold"><Utensils className="w-3.5 h-3.5 text-primary" /> {en ? 'Days with meals logged' : 'Jours avec repas'}</span>
                <span className="text-zinc-400 font-mono text-xs">{weekGoals.nutrition.done}/{weekGoals.nutrition.target}</span>
              </div>
              <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min((weekGoals.nutrition.done / weekGoals.nutrition.target) * 100, 100)}%` }}
                />
              </div>
            </div>
          </div>

          {weekHistory?.weeks?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/10" data-testid="week-goals-history-chart">
              <p className="text-[11px] font-semibold text-zinc-500 mb-2">{en ? 'Last 4 weeks' : '4 dernières semaines'}</p>
              <div className="flex items-end justify-between gap-2 h-16">
                {weekHistory.weeks.slice(-4).map((w, idx, arr) => {
                  const pct = Math.round(
                    ((Math.min(w.workouts.done / (w.workouts.target || 1), 1) + Math.min(w.nutrition.done / w.nutrition.target, 1)) / 2) * 100
                  );
                  const isCurrent = idx === arr.length - 1;
                  const d = new Date(w.week_start);
                  const label = isCurrent ? (en ? 'Now' : 'Cette sem.') : `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
                  return (
                    <div key={w.week_start} className="flex-1 flex flex-col items-center gap-1.5" data-testid={`week-history-bar-${idx}`}>
                      <div className="w-full h-10 flex items-end rounded-md overflow-hidden bg-zinc-800">
                        <div
                          className={`w-full rounded-md transition-all duration-500 ${w.achieved ? 'bg-gradient-to-t from-emerald-500 to-emerald-400' : 'bg-gradient-to-t from-primary/70 to-pink-500/70'}`}
                          style={{ height: `${Math.max(pct, 6)}%`, opacity: pct === 0 ? 0.35 : 1 }}
                        />
                      </div>
                      <span className="text-[10px] text-zinc-500 whitespace-nowrap">{label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Tabs : Aujourd'hui / Progression / Profil */}
      <div className="flex gap-1 sm:gap-1.5 mb-6 bg-zinc-800/60 p-1 rounded-xl">
        {[
          { k: 'today', icon: CalendarDays, fr: "Aujourd'hui", en: 'Today' },
          { k: 'progress', icon: Activity, fr: 'Progression', en: 'Progress' },
          { k: 'profile', icon: UserCog, fr: 'Profil', en: 'Profile' },
        ].map((t) => (
          <button key={t.k} onClick={() => setView(t.k)}
            className={`flex-1 min-w-0 flex items-center justify-center gap-1 sm:gap-1.5 py-2 px-1 rounded-lg text-xs sm:text-sm font-semibold transition-all truncate ${view === t.k ? 'bg-gradient-to-r from-primary to-pink-500 text-white' : 'text-zinc-400 hover:text-white'}`}
            data-testid={`tab-${t.k}`}>
            <t.icon className="w-4 h-4 shrink-0" /> <span className="truncate">{en ? t.en : t.fr}</span>
          </button>
        ))}
      </div>

      {view === 'progress' ? (
        <div className="space-y-6" data-testid="progress-view">
          <NutritionCharts api={api} en={en} />
          <MeasurementsCard api={api} en={en} onChanged={() => { loadDay(); loadWeight(); loadProfileStatus(); }} onWeightAlert={notifyWeightAlert} />
          <ProgressPhotos api={api} authFetch={authFetch} en={en} />
        </div>
      ) : view === 'profile' ? (
        <div data-testid="profile-view">
          <ProfileCard api={api} authFetch={authFetch} en={en} onChanged={() => { loadDay(); loadWeight(); loadProfileStatus(); }} onWeightAlert={notifyWeightAlert} plateauSuggestion={plateauSuggestion} onDismissPlateau={dismissPlateau} />
        </div>
      ) : view === 'detail' ? (
        <DayDetailView summary={summary} entries={day?.entries} en={en} onBack={() => setView('today')} dateLabel={isToday ? (en ? 'Today' : "Aujourd'hui") : dateLabel} />
      ) : (
      <>
      {/* Header : navigation date + streak/gemmes */}
      <GamificationBar
        dateLabel={dateLabel} isToday={isToday}
        onPrev={() => setDate(addDays(date, -1))} onNext={() => setDate(addDays(date, 1))}
        gems={extras.gems} streak={extras.current_streak} workoutStreak={extras.workout_current_streak_weeks} en={en}
      />

      {view === 'today' && <BadgesStrip en={en} badges={extras.badges} />}

      {loading && !day ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
      ) : (
        <div className="space-y-8">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-white">{en ? 'Summary' : 'Résumé'}</h3>
              <button onClick={() => setView('detail')} className="text-sm font-bold text-primary hover:text-pink-400 transition-colors" data-testid="summary-details-link">
                {en ? 'Details' : 'Détails'}
              </button>
            </div>
            <ProgressRings summary={summary} en={en} onScoreInfo={() => setGuide(true)} activityKcal={extras.activity_kcal} />
            <motion.button
              ref={addFoodBtnRef}
              whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
              onClick={() => setSheet({ open: true, meal: null })}
              className="w-full mt-4 relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary to-pink-500 px-5 py-4 flex items-center justify-center gap-2.5 shadow-lg shadow-primary/25"
              data-testid="add-meal-btn"
            >
              <span className="absolute inset-0 bg-[radial-gradient(120px_60px_at_var(--x,30%)_0%,rgba(255,255,255,0.25),transparent)] opacity-60" />
              <span className="relative w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
                <Plus className="w-4.5 h-4.5 text-white" strokeWidth={2.5} />
              </span>
              <span className="relative font-extrabold text-white text-base tracking-tight">{en ? 'Add a meal' : 'Ajouter un repas'}</span>
            </motion.button>
          </div>
          {day?.coaching && <CoachingPanel coaching={day.coaching} en={en} />}
          <GoalsCard api={api} en={en} premium={premium} goals={summary.goals} onUpgrade={onUpgrade} onChanged={loadDay} />

          {/* Alimentation */}
          <div className="min-w-0">
            <div className="flex items-center justify-between gap-2 mb-4">
              <h3 className="font-bold text-white truncate">{en ? 'Food journal' : 'Alimentation'}</h3>
              <div className="flex items-center gap-3 shrink-0">
                <button onClick={() => setView('detail')} className="text-sm font-bold text-primary hover:text-pink-400 transition-colors" data-testid="food-details-link">
                  {en ? 'More' : 'Plus'}
                </button>
                <button
                  onClick={copyYesterday}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-zinc-300 border border-white/10 hover:border-primary/50 hover:text-white transition-colors"
                  data-testid="copy-yesterday-btn"
                >
                  <CopyPlus className="w-3.5 h-3.5" /> {en ? 'Copy yesterday' : 'Copier la veille'}
                </button>
              </div>
            </div>
            <MealList
              entries={day?.entries} onDelete={deleteEntry} onFavorite={favoriteEntry}
              onQuickAdd={(mealKey) => setSheet({ open: true, meal: mealKey })}
              goalCalories={summary.goals.calories} en={en}
            />
          </div>

          {/* Suivi de l'eau */}
          <WaterTracker waterMl={extras.water_ml} goalMl={extras.water_goal_ml} onAdd={addWater} onSetGoal={setWaterGoal} en={en} />

          {/* Données corporelles + activité */}
          <div className="grid sm:grid-cols-2 gap-6">
            <WeightQuickWidget
              latestWeight={lastWeightEntry?.weight} loggedToday={lastWeightEntry?.date === todayStr()}
              onLog={logWeightToday} en={en} onOpenFull={() => setView('progress')}
            />
            <ActivityWidget steps={extras.steps} stepsGoal={extras.steps_goal} kcalBurned={extras.activity_kcal} onAdd={addActivity} en={en} />
          </div>

          {/* Notes / humeur */}
          <MoodWidget mood={extras.mood} noteText={extras.note_text} onSave={saveMood} en={en} />

          <button
            onClick={() => setView('progress')}
            className="w-full rounded-2xl border border-white/10 bg-gradient-to-br from-zinc-900 to-black p-5 text-left hover:border-primary/40 transition-colors group"
            data-testid="goto-progress-card"
          >
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-5 h-5 text-primary" />
              <h3 className="font-bold text-white">{en ? 'Weight history & photos' : 'Historique poids & photos'}</h3>
            </div>
            <p className="text-sm text-zinc-400 mb-4">{en ? 'See your full weight/measurements evolution and before/after photos in the Progress tab.' : 'Consultez l\'évolution complète de votre poids, mensurations et vos photos avant/après dans l\'onglet Progression.'}</p>
            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-primary group-hover:gap-2.5 transition-all">
              {en ? 'Open Progress' : 'Ouvrir Progression'} <ChevronRight className="w-4 h-4" />
            </span>
          </button>
        </div>
      )}
      </>
      )}

      <AddFoodSheet
        open={sheet.open} onClose={() => setSheet((s) => ({ ...s, open: false }))}
        meal={sheet.meal} date={date} api={api} onAdded={() => { loadDay(); loadExtras(); }}
        en={en} premium={premium} onUpgrade={onUpgrade}
      />
      
      {/* Tooltips (uniquement sur l'onglet Aujourd'hui — sinon reste affiché par-dessus les
          autres onglets et bloque les clics, la cible n'existant plus dans le DOM) */}
      {view === 'today' && (
        <SmartTooltip
          id="add-food"
          title={{
            fr: "Ajouter un aliment",
            en: "Add food"
          }}
          description={{
            fr: "Cliquez ici pour ajouter un aliment à votre journal. Vous pouvez rechercher dans notre base de 188 aliments, scanner un code-barre, ou saisir manuellement.",
            en: "Click here to add food to your journal. You can search our database of 188 foods, scan a barcode, or enter manually."
          }}
          targetRef={addFoodBtnRef}
          show={showAddFoodTooltip}
          onDismiss={() => setShowAddFoodTooltip(false)}
          en={en}
        />
      )}

      <GuideModal open={guide} onClose={() => setGuide(false)} en={en} />
    </div>
  );
};

export default TrackingDashboard;

